import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  CalendarClock,
  ChevronDown,
  ClipboardList,
  Clock3,
  KeyRound,
  RotateCcw,
  Users,
} from 'lucide-react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { isAllInspectorsSelected } from '@/components/SchedulePage/ScheduleMap/helpers/selection';
import classes from '@/components/SchedulePage/ScheduleMap/style.module.scss';

const ScheduleMapFilters = ({
  filters,
  layers,
  inspectorOptions,
  onFilterChange,
  onLayerToggle,
  onInspectorToggle,
  onSelectAllInspectors,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const layerOptions = useMemo(
    () => [
      { id: 'scheduledOrders', label: intl.formatMessage({ id: 'SCHEDULE_MAP.LAYERS.ORDERS' }), icon: ClipboardList },
      { id: 'inspectorBases', label: intl.formatMessage({ id: 'SCHEDULE_MAP.LAYERS.INSPECTORS' }), icon: Users },
      { id: 'keyPickups', label: intl.formatMessage({ id: 'SCHEDULE_MAP.LAYERS.KEY_PICKUPS' }), icon: KeyRound },
      { id: 'keyReturns', label: intl.formatMessage({ id: 'SCHEDULE_MAP.LAYERS.KEY_RETURNS' }), icon: RotateCcw },
      { id: 'waitingOrders', label: intl.formatMessage({ id: 'SCHEDULE_MAP.LAYERS.WAITING_ORDERS' }), icon: Clock3 },
    ],
    [intl]
  );

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedInspectorIds = filters.selectedInspectorIds || [];
  const isAllSelected = isAllInspectorsSelected(selectedInspectorIds, inspectorOptions);

  const selectedInspectorOptions = inspectorOptions.filter(
    opt => isAllSelected || selectedInspectorIds.includes(opt.value)
  );

  const displayBadges = selectedInspectorOptions.slice(0, 2);
  const remainingCount = selectedInspectorOptions.length - displayBadges.length;

  return (
    <div className={classes.filtersBar}>
      <div className={classes.filtersLeft}>
        <div className={classes.dropdownContainer} ref={dropdownRef}>
          <button
            type="button"
            className={`${classes.inspectorDropdownButton} ${isOpen ? classes.active : ''}`}
            onClick={() => setIsOpen(prev => !prev)}
            aria-expanded={isOpen}
          >
            <Users aria-hidden="true" className={classes.filterIcon} />
            <span className={classes.filterLabel}>
              {intl.formatMessage({ id: 'SCHEDULE_MAP.FILTER.INSPECTORS_LABEL' })}
            </span>

            <div className={classes.avatarBadgeGroup}>
              {isAllSelected ? (
                <span className={classes.allSelectedText}>
                  {intl.formatMessage({ id: 'SCHEDULE_MAP.FILTER.ALL' })}
                </span>
              ) : selectedInspectorOptions.length === 0 ? (
                <span className={classes.noneSelectedText}>
                  {intl.formatMessage({ id: 'SCHEDULE_MAP.FILTER.NONE' })}
                </span>
              ) : (
                <>
                  {displayBadges.map(opt => (
                    <span
                      key={opt.value}
                      className={classes.avatarBadge}
                      style={{ backgroundColor: opt.color || '#007bff' }}
                      title={opt.label}
                    >
                      {opt.initials}
                    </span>
                  ))}
                  {remainingCount > 0 && <span className={classes.moreBadge}>+{remainingCount}</span>}
                </>
              )}
            </div>

            <ChevronDown className={classes.chevronIcon} />
          </button>

          {isOpen && (
            <div className={classes.dropdownMenu}>
              <div className={classes.dropdownActions}>
                <label className={classes.selectAllLabel}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={e => onSelectAllInspectors(e.target.checked)}
                  />
                  <span>
                    <strong>{intl.formatMessage({ id: 'SCHEDULE_MAP.FILTER.ALL' })}</strong>
                  </span>
                </label>

                {!isAllSelected && selectedInspectorIds.length > 0 && (
                  <button
                    type="button"
                    className={classes.clearSelectionBtn}
                    onClick={() => onSelectAllInspectors(false)}
                  >
                    {intl.formatMessage({ id: 'SCHEDULE_MAP.FILTER.CLEAR_SELECTION' })}
                  </button>
                )}
              </div>

              <div className={classes.dropdownDivider} />

              <div className={classes.inspectorListScroll}>
                {inspectorOptions.map(opt => {
                  const isChecked = isAllSelected || selectedInspectorIds.includes(opt.value);

                  return (
                    <label key={opt.value} className={classes.dropdownOption}>
                      <div className={classes.optionLeft}>
                        <span
                          className={classes.inspectorAvatar}
                          style={{ backgroundColor: opt.color || '#007bff' }}
                        >
                          {opt.initials}
                        </span>
                        <div className={classes.inspectorMeta}>
                          <span className={classes.inspectorName}>{opt.label}</span>
                          {opt.document && <span className={classes.inspectorDoc}>{opt.document}</span>}
                        </div>
                      </div>

                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onInspectorToggle(opt.value)}
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <label className={classes.filterSelectLabel}>
          <CalendarClock aria-hidden="true" className={classes.filterIcon} />
          <span className={classes.filterLabel}>
            {intl.formatMessage({ id: 'SCHEDULE_MAP.FILTER.SCHEDULE_TYPE_LABEL' })}
          </span>
          <select
            className={classes.filterSelect}
            value={filters.scheduleType}
            onChange={event => onFilterChange('scheduleType', event.target.value)}
          >
            <option value="all">{intl.formatMessage({ id: 'SCHEDULE_MAP.FILTER.ALL' })}</option>
            <option value="fixed">{intl.formatMessage({ id: 'SCHEDULE_MAP.FILTER.SCHEDULE_FIXED' })}</option>
            <option value="flexible">{intl.formatMessage({ id: 'SCHEDULE_MAP.FILTER.SCHEDULE_FLEXIBLE' })}</option>
          </select>
        </label>
      </div>

      <div className={classes.divider} aria-hidden="true" />

      <div className={classes.layersGroup}>
        {layerOptions.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`${classes.layerPill} ${layers[id] ? classes.layerPillActive : ''}`}
            aria-pressed={layers[id]}
            onClick={() => onLayerToggle(id)}
          >
            <Icon aria-hidden="true" className={classes.layerIcon} />
            <span>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const optionPropType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  initials: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
});

ScheduleMapFilters.propTypes = {
  filters: PropTypes.shape({
    selectedInspectorIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    scheduleType: PropTypes.string.isRequired,
  }).isRequired,
  layers: PropTypes.objectOf(PropTypes.bool).isRequired,
  inspectorOptions: PropTypes.arrayOf(optionPropType).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onLayerToggle: PropTypes.func.isRequired,
  onInspectorToggle: PropTypes.func.isRequired,
  onSelectAllInspectors: PropTypes.func.isRequired,
};

export default ScheduleMapFilters;
